import styled from "@emotion/styled";
import { useEffect, useState } from "react";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";

import { ChevronDown } from "@livechat/design-system-icons";
import {
  ActionMenu,
  ActionMenuItem,
  Avatar,
  Button,
  DesignToken,
  IActionMenuOption,
  Icon,
  Modal,
  SpacingToken,
  Text,
} from "@livechat/design-system-react-components";

import { ButtonGroup } from "components/button/ButtonGroup";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { InviteButton } from "components/payment-failures-limits/InviteButton";
import { PaymentFailedAlert, PaymentFailedLocation } from "components/payment-failures-limits/PaymentFailedAlert";
import { SeatsExceededMessage } from "components/payment-failures-limits/SeatsExceededMessage";
import { ResponsiveBreakpoint } from "constants/responsive-breakpoint";
import Role from "constants/role";
import { helpDeskRoleLabels, mapLiveChatRoleToHelpDeskRole } from "helpers/agent";
import { willUpgradeSubscription } from "helpers/subscription";
import { usePaymentFailure } from "hooks/use-payment-failure";
import { usePrevious } from "hooks/use-previous";
import { getNonViewersCount, selectIsAdmin } from "store/entities/agents";
import { getBilling } from "store/entities/billing";
import { selectIsManagedByShopify } from "store/entities/integrations";
import { selectManagedByPartner } from "store/entities/license";
import { selectCurrentSubscription } from "store/entities/subscriptions";
import { AgentsViewActions, ILiveChatAgent, selectIsImportingLiveChatAgent } from "store/views/agents";
import { textEllipsisStyles } from "styles/common";
import { withVar } from "themes/helpers";

interface IProps {
  agent: ILiveChatAgent | null;
  onModalClose(): void;
}

const LiveChatAgentModal: React.FC<IProps> = ({ agent, onModalClose }) => {
  const dispatch = useDispatch();
  const onImport = (email: string, role: Role): void => {
    dispatch(AgentsViewActions.importLiveChatAgent({ email, role }));
  };
  const nonViewersCount = useSelector(getNonViewersCount);
  const isImportingLiveChatAgent = useSelector(selectIsImportingLiveChatAgent);
  const isOwner = useSelector(selectIsAdmin);
  const isManagedByShopify = useSelector(selectIsManagedByShopify);
  const isManagedByPartner = useSelector(selectManagedByPartner);
  const subscription = useSelector(selectCurrentSubscription);
  const billing = useSelector(getBilling);

  const prevIsImportingLiveChatAgent = usePrevious<boolean>(isImportingLiveChatAgent);
  const { failed } = usePaymentFailure();

  const [agentRole, setAgentRole] = useState<Role>(
    isOwner && agent ? mapLiveChatRoleToHelpDeskRole(agent.permission) : Role.Normal
  );

  const isManaged = isManagedByPartner || isManagedByShopify;
  const willUpgrade = willUpgradeSubscription(
    subscription,
    billing,
    nonViewersCount,
    agentRole === Role.Viewer ? 0 : 1,
    isManaged
  );
  const options: IActionMenuOption[] = [
    ...(isOwner
      ? [
          {
            key: Role.Owner,
            element: <ActionMenuItem>Admin</ActionMenuItem>,
            onClick: () => handleItemSelect(Role.Owner),
          },
        ]
      : []),
    { key: Role.Normal, element: <ActionMenuItem>Agent</ActionMenuItem>, onClick: () => handleItemSelect(Role.Normal) },
    {
      key: Role.Viewer,
      element: <ActionMenuItem>Viewer</ActionMenuItem>,
      onClick: () => handleItemSelect(Role.Viewer),
    },
  ];

  const handleItemSelect = (itemKey: Role): void => {
    setAgentRole(itemKey);
  };

  const handleSubmit = (): void => {
    if (agent) {
      onImport(agent.email, agentRole);
    }
  };

  useEffect(() => {
    if (!isImportingLiveChatAgent && prevIsImportingLiveChatAgent && !failed) {
      onModalClose();
    }
  }, [isImportingLiveChatAgent, onModalClose, failed, prevIsImportingLiveChatAgent]);

  return (
    <Modal
      onClose={onModalClose}
      heading="Invite agent to HelpDesk"
      footer={
        <ButtonGroup>
          <Button onClick={onModalClose} kind="secondary" disabled={isImportingLiveChatAgent}>
            Cancel
          </Button>
          <InviteButton
            limitExceeded={willUpgrade}
            failed={failed}
            onSubmit={handleSubmit}
            isSaving={isImportingLiveChatAgent}
            label={willUpgrade ? "Invite and pay" : "Invite"}
          />
        </ButtonGroup>
      }
    >
      <Wrapper>
        <Text>Add your LiveChat agent to your HelpDesk subscription.</Text>
        <Agent>
          <Block marginRight="12px">
            <Avatar src={agent?.avatar} text={agent?.name || agent?.email} type={agent?.avatar ? "image" : "text"} />
          </Block>
          <AgentMeta column flex={1} justifyContent="center" marginRight="30px">
            <Text customColor={withVar(DesignToken.ContentBasicPrimary)} css={textEllipsisStyles}>
              {agent?.name || agent?.email}
            </Text>
            <Text size="sm" customColor={withVar(DesignToken.ContentBasicSecondary)} css={textEllipsisStyles}>
              {agent?.email}
            </Text>
          </AgentMeta>
          <ActionMenu
            options={options}
            selectedOptions={[agentRole]}
            placement="bottom-start"
            triggerRenderer={
              <Button kind="secondary" icon={<Icon source={ChevronDown} />} iconPosition="right">
                {helpDeskRoleLabels[agentRole]}
              </Button>
            }
          />
        </Agent>
        {willUpgrade && (
          <Block paddingTop={withVar(SpacingToken.Spacing8)}>
            <SeatsExceededMessage limitExceededBy={1} />
          </Block>
        )}
        {failed && (
          <Block maxWidth="430px" paddingTop={withVar(SpacingToken.Spacing8)}>
            <PaymentFailedAlert location={PaymentFailedLocation.InviteLC} />
          </Block>
        )}
      </Wrapper>
    </Modal>
  );
};

const Wrapper = styled.div`
  max-width: 440px;
`;

const Agent = styled(Flex)`
  margin-top: var(${SpacingToken.Spacing7});
  align-items: center;
  justify-content: space-between;
`;

const AgentMeta = styled(Flex)`
  max-width: 260px;
  @media (max-width: ${ResponsiveBreakpoint.Mobile}px) {
    max-width: 182px;
  }
`;

export default LiveChatAgentModal;
